package co.maytes.standupbot.dashboard.features.releases

import co.maytes.standupbot.shared.dto.ship.ReleaseSettingsDto
import co.maytes.standupbot.shared.dto.ship.ShipEventDto
import kotlinx.datetime.Instant
import kotlinx.datetime.TimeZone

enum class ReleaseZoneChoice(val label: String) {
    Workspace("Workspace time"),
    Mine("My time"),
    Utc("UTC"),
}

fun resolveReleaseZone(
    choice: ReleaseZoneChoice,
    workspaceZoneId: String?,
    systemZone: TimeZone = TimeZone.currentSystemDefault(),
): TimeZone =
    when (choice) {
        ReleaseZoneChoice.Workspace -> workspaceZoneId?.let { runCatching { TimeZone.of(it) }.getOrNull() } ?: TimeZone.UTC
        ReleaseZoneChoice.Mine -> systemZone
        ReleaseZoneChoice.Utc -> TimeZone.UTC
    }

fun shipEventSubtitle(event: ShipEventDto): String =
    when (event.kind) {
        ShipEventDto.KIND_RELEASE -> "${event.repo} release ${event.ref}"
        else -> "${event.repo} #${event.ref}"
    }

sealed interface GithubReach {
    data object Unknown : GithubReach

    data object NotConnected : GithubReach

    data class Connected(val repoCount: Int) : GithubReach
}

private const val HTTP_NOT_FOUND = 404

fun githubReach(
    status: Int?,
    repoCount: Int?,
): GithubReach =
    when {
        repoCount != null -> if (repoCount > 0) GithubReach.Connected(repoCount) else GithubReach.NotConnected
        status == HTTP_NOT_FOUND -> GithubReach.NotConnected
        else -> GithubReach.Unknown
    }

sealed interface ReleaseEmptyState {
    data object NoGithub : ReleaseEmptyState

    data object TurnOn : ReleaseEmptyState

    data object NothingYet : ReleaseEmptyState

    data object AskAdmin : ReleaseEmptyState
}

fun releaseEmptyState(
    isAdmin: Boolean,
    settings: ReleaseSettingsDto?,
    github: GithubReach = GithubReach.Unknown,
): ReleaseEmptyState =
    when {
        !isAdmin -> ReleaseEmptyState.AskAdmin
        github == GithubReach.NotConnected -> ReleaseEmptyState.NoGithub
        settings?.mergedFeedEnabled == true -> ReleaseEmptyState.NothingYet
        else -> ReleaseEmptyState.TurnOn
    }

fun zoneLabel(
    instant: Instant,
    zone: TimeZone,
): String {
    val zoneId = if (zone == TimeZone.UTC) "UTC" else zone.id
    val epochMillis = instant.toEpochMilliseconds().toDouble()
    return try {
        js(
            "Intl.DateTimeFormat('en-AU', { timeZone: zoneId, timeZoneName: 'short' })" +
                ".formatToParts(new Date(epochMillis))" +
                ".filter(function (part) { return part.type === 'timeZoneName'; })[0].value",
        ) as String
    } catch (_: Throwable) {
        zoneId
    }
}
